import { Badge, Card, DataState, InfoRow, PageHeader } from "@vms/ui-web";
import {
  GAME_LABELS,
  RF2_FIELDS,
  RF2_FIELD_GROUPS,
  type BrandingTemplate,
  type DifficultyTemplate,
  type Game,
  type RaceTemplate,
  type Rf2FieldDef,
  type Rf2RaceTemplateContent,
} from "@vms/types";
import { useOrgContext } from "../../context/OrgContext";
import {
  useBrandingTemplates,
  useDifficultyTemplates,
  useRaceTemplates,
} from "../../api/templates";

const KIND_COPY: Record<string, { title: string; description: string }> = {
  branding: {
    title: "Branding templates",
    description: "Logo, colours, hero imagery and overlay copy.",
  },
  race: {
    title: "Race templates",
    description: "AI, race format, realism and rules applied to each session.",
  },
  difficulty: {
    title: "Difficulty templates",
    description: "Driver-aid presets exposed during car selection.",
  },
};

export function Templates({ kind }: { kind: "branding" | "race" | "difficulty" }) {
  const { currentOrg } = useOrgContext();
  if (!currentOrg) return null;
  const copy = KIND_COPY[kind];

  return (
    <div className="space-y-4">
      <PageHeader title={copy.title} subtitle={copy.description} />
      <ReadOnlyNotice orgName={currentOrg.name} />
      {kind === "branding" && <BrandingView orgId={currentOrg.id} />}
      {kind === "race" && <RaceView orgId={currentOrg.id} />}
      {kind === "difficulty" && <DifficultyView orgId={currentOrg.id} />}
    </div>
  );
}

function ReadOnlyNotice({ orgName }: { orgName: string }) {
  return (
    <Card className="p-3">
      <p className="text-sm text-gray-600">
        Read-only. Template authoring lives on each venue's hub UI; changes sync up to the cloud
        automatically and propagate to every hub in {orgName} (last-writer-wins on the cloud copy).
      </p>
    </Card>
  );
}

// ── Shared bits ─────────────────────────────────────────────────────────────

function TemplateBadges({ isDefault, isSystem }: { isDefault: boolean; isSystem: boolean }) {
  return (
    <span className="flex items-center gap-1.5">
      {isDefault && <Badge className="bg-indigo-100 text-indigo-800">Default</Badge>}
      {isSystem && <Badge className="bg-gray-100 text-gray-700">System</Badge>}
    </span>
  );
}

function TemplateHeader({
  name,
  isDefault,
  isSystem,
  trailing,
}: {
  name: string;
  isDefault: boolean;
  isSystem: boolean;
  trailing?: React.ReactNode;
}) {
  return (
    <div className="flex items-center justify-between gap-3 border-b border-gray-100 pb-3">
      <div className="flex items-center gap-2">
        <h3 className="font-semibold text-gray-900">{name}</h3>
        {trailing}
      </div>
      <TemplateBadges isDefault={isDefault} isSystem={isSystem} />
    </div>
  );
}

function Swatch({ label, color }: { label: string; color: string | null | undefined }) {
  if (!color) return null;
  return (
    <div className="flex items-center gap-2">
      <span
        className="inline-block h-5 w-5 rounded border border-gray-300"
        style={{ backgroundColor: color }}
      />
      <span className="text-xs text-gray-600">{label}</span>
      <span className="font-mono text-xs text-gray-400">{color}</span>
    </div>
  );
}

function Thumb({ label, url }: { label: string; url: string | null | undefined }) {
  if (!url) return null;
  return (
    <div className="space-y-1">
      <img
        src={url}
        alt={label}
        className="h-20 w-20 rounded border border-gray-200 bg-gray-50 object-contain"
      />
      <div className="text-xs text-gray-500">{label}</div>
    </div>
  );
}

function SwatchGroup({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <div className="space-y-2">
      <div className="text-xs font-medium uppercase tracking-wide text-gray-400">{title}</div>
      <div className="flex flex-wrap gap-x-6 gap-y-2">{children}</div>
    </div>
  );
}

// ── Branding ────────────────────────────────────────────────────────────────

function BrandingView({ orgId }: { orgId: string }) {
  const { data, loading, error } = useBrandingTemplates(orgId);
  return (
    <DataState
      loading={loading}
      error={error}
      empty={!data || data.length === 0}
      emptyMessage="No branding templates have synced from this organisation's hubs yet."
    >
      <div className="space-y-4">
        {(data ?? []).map((t) => (
          <BrandingCard key={t.id} t={t} />
        ))}
      </div>
    </DataState>
  );
}

function BrandingCard({ t }: { t: BrandingTemplate }) {
  return (
    <Card className="space-y-4 p-5">
      <TemplateHeader name={t.name} isDefault={t.isDefault} isSystem={t.isSystem} />

      {(t.logoUrl ||
        t.backgroundImageUrl ||
        t.overlayWelcomeBgUrl ||
        t.overlayLoadingBgUrl ||
        t.overlayTrainingBgUrl ||
        t.overlayResultsBgUrl) && (
        <div className="flex flex-wrap gap-4">
          <Thumb label="Logo" url={t.logoUrl} />
          <Thumb label="Background" url={t.backgroundImageUrl} />
          <Thumb label="Overlay · welcome" url={t.overlayWelcomeBgUrl} />
          <Thumb label="Overlay · loading" url={t.overlayLoadingBgUrl} />
          <Thumb label="Overlay · training" url={t.overlayTrainingBgUrl} />
          <Thumb label="Overlay · results" url={t.overlayResultsBgUrl} />
        </div>
      )}

      <SwatchGroup title="Leaderboard">
        <Swatch label="Background" color={t.backgroundColor} />
        <Swatch label="P1 bg" color={t.pos1BgColor} />
        <Swatch label="P1 text" color={t.pos1TextColor} />
        <Swatch label="P2 bg" color={t.pos2BgColor} />
        <Swatch label="P2 text" color={t.pos2TextColor} />
        <Swatch label="P3 bg" color={t.pos3BgColor} />
        <Swatch label="P3 text" color={t.pos3TextColor} />
        <Swatch label="Default bg" color={t.defaultBgColor} />
        <Swatch label="Default text" color={t.defaultTextColor} />
      </SwatchGroup>

      <SwatchGroup title="Overlay">
        <Swatch label="Gradient start" color={t.overlayGradientStart} />
        <Swatch label="Gradient end" color={t.overlayGradientEnd} />
        <Swatch label="Accent" color={t.overlayAccentColor} />
        <Swatch label="Text" color={t.overlayTextColor} />
      </SwatchGroup>

      <SwatchGroup title="Buttons">
        <Swatch label="Primary" color={t.primaryButtonColor} />
        <Swatch label="Primary text" color={t.primaryButtonTextColor} />
        <Swatch label="Secondary" color={t.secondaryButtonColor} />
        <Swatch label="Secondary text" color={t.secondaryButtonTextColor} />
      </SwatchGroup>

      <dl className="space-y-1.5 border-t border-gray-100 pt-3 text-sm">
        <InfoRow
          label="Training screen"
          value={
            t.showTraining
              ? `On · ${t.trainingDurationSeconds ?? 20}s`
              : "Off"
          }
        />
        {t.registrationFooterText && (
          <InfoRow label="Registration footer" value={t.registrationFooterText} />
        )}
        {t.registrationThankYouText && (
          <InfoRow label="Registration thank-you" value={t.registrationThankYouText} />
        )}
      </dl>
    </Card>
  );
}

// ── Race ────────────────────────────────────────────────────────────────────

function RaceView({ orgId }: { orgId: string }) {
  const { data, loading, error } = useRaceTemplates(orgId);
  return (
    <DataState
      loading={loading}
      error={error}
      empty={!data || data.length === 0}
      emptyMessage="No race templates have synced from this organisation's hubs yet."
    >
      <div className="space-y-4">
        {(data ?? []).map((t) => (
          <RaceCard key={t.id} t={t} />
        ))}
      </div>
    </DataState>
  );
}

function RaceCard({ t }: { t: RaceTemplate }) {
  return (
    <Card className="space-y-4 p-5">
      <TemplateHeader
        name={t.name}
        isDefault={t.isDefault}
        isSystem={t.isSystem}
        trailing={
          <span className="flex items-center gap-1.5">
            <Badge className="bg-blue-100 text-blue-800">{GAME_LABELS[t.game] ?? t.game}</Badge>
            <Badge className="bg-gray-100 text-gray-700 capitalize">{t.mode}</Badge>
          </span>
        }
      />
      {t.game === "rfactor-2" ? <Rf2RaceBody content={t.content} /> : <AcRaceBody content={t.content} />}
    </Card>
  );
}

function AcRaceBody({ content }: { content: string }) {
  if (!content.trim()) return <p className="text-sm text-gray-400">No content.</p>;
  return (
    <pre className="overflow-x-auto rounded-md bg-gray-50 p-3 text-xs text-gray-700">
      {content}
    </pre>
  );
}

function Rf2RaceBody({ content }: { content: string }) {
  const parsed = parseRf2(content);
  if (!parsed) {
    // Couldn't decode the structured form — show the raw JSON so nothing is hidden.
    return <AcRaceBody content={content} />;
  }

  const groups = RF2_FIELD_GROUPS.map((group) => {
    const fields = RF2_FIELDS.filter(
      (f) => f.group === group && parsed[f.field] !== undefined && parsed[f.field] !== null,
    );
    // numOpponents is intentionally absent from RF2_FIELDS; surface it in the AI group.
    const showOpponents = group === "AI" && typeof parsed.numOpponents === "number";
    return { group, fields, showOpponents };
  }).filter((g) => g.fields.length > 0 || g.showOpponents);

  const extraSettings = parsed.sessionSettings
    ? Object.entries(parsed.sessionSettings)
    : [];

  if (groups.length === 0 && extraSettings.length === 0) {
    return <p className="text-sm text-gray-400">No settings configured — sims keep their current values.</p>;
  }

  return (
    <div className="grid grid-cols-1 gap-x-8 gap-y-4 sm:grid-cols-2">
      {groups.map(({ group, fields, showOpponents }) => (
        <div key={group} className="space-y-1.5">
          <div className="text-xs font-medium uppercase tracking-wide text-gray-400">{group}</div>
          <dl className="space-y-1 text-sm">
            {showOpponents && <InfoRow label="AI Opponents" value={String(parsed.numOpponents)} />}
            {fields.map((f) => (
              <InfoRow
                key={f.field}
                label={f.label}
                value={renderRf2Value(f, parsed[f.field] as number | boolean)}
              />
            ))}
          </dl>
        </div>
      ))}
      {extraSettings.length > 0 && (
        <div className="space-y-1.5 sm:col-span-2">
          <div className="text-xs font-medium uppercase tracking-wide text-gray-400">
            Custom session settings
          </div>
          <dl className="space-y-1 text-sm">
            {extraSettings.map(([key, val]) => (
              <InfoRow key={key} label={key} value={String(val)} />
            ))}
          </dl>
        </div>
      )}
    </div>
  );
}

function parseRf2(content: string): Rf2RaceTemplateContent | null {
  try {
    const o = JSON.parse(content);
    return o && typeof o === "object" && !Array.isArray(o) ? (o as Rf2RaceTemplateContent) : null;
  } catch {
    return null;
  }
}

function renderRf2Value(def: Rf2FieldDef, value: number | boolean): string {
  if (def.kind === "bool") return value ? "Yes" : "No";
  if (def.kind === "enum") {
    const opt = def.options?.find((o) => o.value === value);
    return opt ? opt.label : String(value);
  }
  if (def.kind === "gridPosition") return value === 0 ? "Random" : String(value);
  if (def.kind === "percent") return `${value}%`;
  return String(value);
}

// ── Difficulty ──────────────────────────────────────────────────────────────

function DifficultyView({ orgId }: { orgId: string }) {
  const { data, loading, error } = useDifficultyTemplates(orgId);
  return (
    <DataState
      loading={loading}
      error={error}
      empty={!data || data.length === 0}
      emptyMessage="No difficulty templates have synced from this organisation's hubs yet."
    >
      <div className="space-y-4">
        {(data ?? []).map((t) => (
          <DifficultyCard key={t.id} t={t} />
        ))}
      </div>
    </DataState>
  );
}

function DifficultyCard({ t }: { t: DifficultyTemplate }) {
  return (
    <Card className="space-y-4 p-5">
      <TemplateHeader
        name={t.name}
        isDefault={t.isDefault}
        isSystem={t.isSystem}
        trailing={
          <Badge className="bg-blue-100 text-blue-800">{gameLabel(t.game)}</Badge>
        }
      />
      <div className="flex flex-wrap gap-4">
        <Thumb label="Image" url={t.imageUrl} />
        <div className="min-w-0 flex-1">
          {t.content.trim() ? (
            <pre className="overflow-x-auto rounded-md bg-gray-50 p-3 text-xs text-gray-700">
              {t.content}
            </pre>
          ) : (
            <p className="text-sm text-gray-400">No content.</p>
          )}
        </div>
      </div>
    </Card>
  );
}

function gameLabel(game: Game): string {
  return GAME_LABELS[game] ?? game;
}
